/*
 * video-autoplay-rescue.js
 *
 * Make autoplay videos actually play, everywhere.
 *
 * The hero slideshow's <video autoplay muted playsinline> relies on
 * the HTML attribute alone (halo.slide-show.js only manages videos
 * for multi-slide sliders). The attribute is ignored in plenty of
 * real-world cases: iOS Low Power Mode, Chrome/Android data saver,
 * some in-app browsers, or when the element was touched by DOM moves
 * before playback started. Result: visitors see the poster forever.
 *
 * Strategy, cheapest first:
 *  1. Force muted + playsinline as PROPERTIES (attribute alone is
 *     not always honoured after scripts touch the element).
 *  2. Call play() as soon as each video can play, when it scrolls
 *     into view, and when the tab becomes visible again.
 *  3. If the browser still refuses (autoplay policy), retry once on
 *     the first touch/click/scroll anywhere on the page. That counts
 *     as a user gesture, so play() is then allowed.
 */
(function () {
  'use strict';

  function tryPlay(v) {
    if (!v.paused || v.ended) return;
    // Autoplay is only allowed muted; enforce as properties.
    v.muted = true;
    v.playsInline = true;
    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () { /* blocked; gesture fallback will retry */ });
    }
  }

  function allAutoplayVideos() {
    return Array.prototype.slice.call(
      document.querySelectorAll('video[autoplay]')
    );
  }

  function playVisible() {
    allAutoplayVideos().forEach(function (v) {
      var r = v.getBoundingClientRect();
      var visible = r.bottom > 0 && r.top < window.innerHeight && r.width > 0;
      if (visible) tryPlay(v);
    });
  }

  // Poster shield: iOS paints a native play button over videos it
  // won't autoplay (Low Power Mode / Reduce Motion) and modern iOS
  // ignores the ::-webkit-media-controls CSS that used to hide it.
  // Instead, layer the poster image ON TOP of the video and only
  // melt it away when playback genuinely starts ('playing' event).
  // The native button renders underneath the shield, so it is never
  // visible. pointer-events: none keeps taps working (they feed the
  // gesture fallback below).
  function addPosterShield(v) {
    var parent = v.parentElement;
    if (!parent) return;

    // Prefer a server-rendered shield (slide-show.liquid emits one so
    // it exists at first paint and the native button can never flash).
    var shield = parent.querySelector('.k-poster-shield');

    if (shield) {
      if (!v.paused || v.currentTime > 0) {
        // Video already started before we armed: drop the shield now.
        if (shield.parentElement) shield.parentElement.removeChild(shield);
        return;
      }
    } else {
      // No server-rendered shield (other sections): build one from the
      // poster attribute. Late by one paint, still better than nothing.
      var poster = v.getAttribute('poster');
      if (!poster) return;                       // desktop hero has none
      if (!v.paused || v.currentTime > 0) return; // already playing
      shield = document.createElement('img');
      shield.className = 'k-poster-shield';
      shield.src = poster;
      shield.alt = '';
      shield.setAttribute('aria-hidden', 'true');
      shield.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;' +
        'object-fit:cover;object-position:center;z-index:2;' +
        'pointer-events:none;opacity:1;transition:opacity .35s ease;';
      if (getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(shield);
    }

    v.addEventListener('playing', function removeShield() {
      v.removeEventListener('playing', removeShield);
      shield.style.opacity = '0';
      setTimeout(function () {
        if (shield.parentElement) shield.parentElement.removeChild(shield);
      }, 400);
    });
  }

  function armVideo(v) {
    if (v.dataset.kAutoplayArmed === '1') return;
    v.dataset.kAutoplayArmed = '1';
    addPosterShield(v);
    // Play as soon as enough data is buffered.
    v.addEventListener('canplay', function () { tryPlay(v); });
    v.addEventListener('loadeddata', function () { tryPlay(v); });
    // Some browsers pause off-screen/backgrounded videos and never
    // resume them; watch visibility.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) tryPlay(entry.target);
        });
      }, { threshold: 0.1 }).observe(v);
    }
    tryPlay(v);
  }

  function init() {
    allAutoplayVideos().forEach(armVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', playVisible);

  // Tab returns to foreground: resume.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) playVisible();
  });

  // Autoplay-policy fallback: the first real user gesture unlocks
  // playback; use it to start anything still frozen on its poster.
  ['touchstart', 'touchend', 'click', 'scroll', 'keydown'].forEach(function (evt) {
    window.addEventListener(evt, function onFirstGesture() {
      window.removeEventListener(evt, onFirstGesture);
      playVisible();
    }, { passive: true, once: true });
  });

  // Videos inserted later (sections re-rendered in the editor, lazy
  // content): arm them as they appear.
  new MutationObserver(function () {
    if (document.querySelector('video[autoplay]:not([data-k-autoplay-armed])')) {
      init();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
